// Клиент ручек страницы «Настройки → Организация».
//
// Имя организации — `PATCH /api/v1/organization` (ядро, право
// `org.profile.manage`); бюджет повторов — `GET/PUT/DELETE
// /api/v1/settings/retries` (модуль settings, права `settings.org.*`).
// Имена полей берём 1:1 из pydantic-схем бэкенда. JWT прикручивает
// `authedFetch`.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";

// HTTP-код «значение не прошло серверную валидацию» (тело с `detail`).
export const HTTP_UNPROCESSABLE_ENTITY = 422;

// HTTP-код «прав недостаточно».
export const HTTP_FORBIDDEN = 403;

const ORGANIZATION_URL = "/api/v1/organization";
const RETRIES_URL = "/api/v1/settings/retries";

// Ответ `PATCH /organization`: сохранённое (обрезанное) имя; slug ручка
// не меняет.
export interface OrganizationResponse {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
}

export interface RenameOrganizationRequest {
  readonly name: string;
}

// Состояние бюджета повторов организации. `org_max_retries` — сохранённое
// значение как есть (`null` — не задано; при непустой причине — значение,
// не прошедшее схему, поэтому тип `unknown`).
export interface RetryBudgetResponse {
  readonly org_max_retries: unknown;
  readonly effective_max_retries: number;
  readonly default_max_retries: number;
  readonly min: number;
  readonly max: number;
  readonly org_max_retries_invalid_reason: string | null;
}

export interface SetRetryBudgetRequest {
  readonly max_retries: number;
}

/**
 * HTTP-ошибка с числовым `status`. Форма различает 422 (значение не
 * прошло валидацию, `detail` в теле) и 403 от прочих кодов.
 */
export class HttpError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  public constructor(status: number, message: string, detail: string | null) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.detail = detail;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Бросить HttpError с `detail` тела ошибки FastAPI: строка — причина
// валидатора; список ошибок pydantic для людей не годится и даёт `null`.
async function throwHttp(response: Response, label: string): Promise<never> {
  const detail = await readErrorDetail(response);
  throw new HttpError(
    response.status,
    `${label}: HTTP ${String(response.status)}`,
    detail,
  );
}

export async function renameOrganization(
  body: RenameOrganizationRequest,
): Promise<OrganizationResponse> {
  const response = await authedFetch(ORGANIZATION_URL, {
    method: "PATCH",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await throwHttp(response, "renameOrganization");
  }
  return (await response.json()) as OrganizationResponse;
}

export async function fetchRetryBudget(): Promise<RetryBudgetResponse> {
  const response = await authedFetch(RETRIES_URL, { headers: buildHeaders() });
  if (!response.ok) {
    await throwHttp(response, "fetchRetryBudget");
  }
  return (await response.json()) as RetryBudgetResponse;
}

export async function setRetryBudget(
  body: SetRetryBudgetRequest,
): Promise<RetryBudgetResponse> {
  const response = await authedFetch(RETRIES_URL, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await throwHttp(response, "setRetryBudget");
  }
  return (await response.json()) as RetryBudgetResponse;
}

export async function resetRetryBudget(): Promise<RetryBudgetResponse> {
  const response = await authedFetch(RETRIES_URL, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    await throwHttp(response, "resetRetryBudget");
  }
  return (await response.json()) as RetryBudgetResponse;
}
